import React, { useEffect } from 'react';
import formatFileCategory from '../../../commom/formatFileCategory';
import { useStampedPdf } from '../../../hooks/useStampedPdf';
import { Button } from '../../atoms/Button';
import { Pdf } from '../../atoms/Pdf';
import PageHeader from '../../molecules/Header';
import { Typography } from '../../molecules/Typography';
import { Breadcrumbs, BreadcrumbsItem } from '../Breadcrumbs';
import { ContentActions } from '../ContentActions';
import { IFavoriteCategory } from '../ContentActions/FavoriteButton';

const STAMPED_PDF_ERROR_MESSAGES: Record<string, string> = {
  unauthorized: 'Faça login para visualizar este material.',
  forbidden: 'Conteúdo indisponível para o seu plano.',
  'not-found': 'Material não encontrado.',
  generic: 'Não foi possível carregar o material. Tente novamente.',
};

/** Considera PDF quando a uri (ignorando query string) termina em .pdf */
function isPdfFile(uri?: string) {
  if (!uri) return false;
  return /\.pdf($|\?)/i.test(uri);
}

/**
 * Arquivos Office (planilhas, documentos, apresentações) não abrem inline no
 * navegador: o iframe dispararia um download. Mesmo critério do app mobile.
 */
function isOfficeFile(uri?: string) {
  if (!uri) return false;
  return /\.(xlsx?|docx?|pptx?)($|\?)/i.test(uri);
}

/**
 * Monta a URL do visualizador do Google Docs (mesmo usado no app mobile) para
 * exibir arquivos Office dentro do iframe. Exige que a uri seja pública.
 */
function buildOfficeViewerUrl(uri: string) {
  return `https://docs.google.com/gview?url=${encodeURIComponent(uri)}&embedded=true`;
}

interface IMaterial {
  title: string;
  discipline?: string;
  file: string;
  materialType: string;
  materialId: string;
  materialSlug: string;
  review: 'like' | 'dislike' | undefined;
  isFavorite?: boolean;
  showFavorite?: boolean;
  breadcrumb?: Array<BreadcrumbsItem>;
  markMaterialView?: () => void;
}

const Material = React.memo(function Material({
  title,
  materialSlug,
  discipline,
  file,
  materialType,
  materialId,
  breadcrumb = [],
  isFavorite,
  review,
  showFavorite = true,
  markMaterialView,
}: IMaterial) {
  useEffect(() => {
    if (materialId && markMaterialView) {
      markMaterialView();
    }
  }, [materialId]);


  file = file && file?.replace(
    '{STORAGE_BASE_URL}',
    'https://storage.aprovatotal.com.br'
  );

  const isPdf = isPdfFile(file);

  // PDFs são servidos pelo endpoint carimbado (marca d'água por aluno/acesso);
  // demais tipos (áudio etc.) continuam usando a uri original como fallback.
  const {
    url: pdfUrl,
    isLoading: isPdfLoading,
    error: pdfError,
  } = useStampedPdf({ materialId, fallbackUrl: file, enabled: isPdf });

  // Planilhas/documentos Office passam pelo visualizador do Google; o botão
  // "Abrir em nova guia" continua apontando para o arquivo original (download).
  const viewerUrl =
    pdfUrl && !isPdf && isOfficeFile(pdfUrl)
      ? buildOfficeViewerUrl(pdfUrl)
      : pdfUrl;

  return (
    <div>
      <Breadcrumbs items={breadcrumb} className='pb-12' />

      <div className='pb-8'>
        <PageHeader title={title} module={discipline}>
          <div className='flex flex-col justify-between gap-md md:flex-row'>
            <div className='flex items-center gap-3'>
              <Typography size='small' className='capitalize'>
                {formatFileCategory(materialType)}
              </Typography>
            </div>

            <ContentActions.Root
              contentType={'Material'}
              contentId={materialId}
              contentSlug={materialSlug}
              className={'ml-auto'}
            >
              <ContentActions.ReviewButtons defaultReview={review} />
              {showFavorite && (
                <ContentActions.FavoriteButton
                  defaultFavorite={isFavorite}
                  category={materialType as IFavoriteCategory}
                />
              )}
              <ContentActions.Report />
            </ContentActions.Root>
          </div>
        </PageHeader>
      </div>

      <div>
        {isPdfLoading ? (
          <div className='flex h-screen w-full items-center justify-center'>
            <Typography size='small'>Carregando material...</Typography>
          </div>
        ) : pdfError ? (
          <div className='flex h-screen w-full items-center justify-center'>
            <Typography size='small'>
              {STAMPED_PDF_ERROR_MESSAGES[pdfError]}
            </Typography>
          </div>
        ) : (
          <Pdf file={viewerUrl} title={title} />
        )}
      </div>

      {pdfUrl && !pdfError && (
        <div className='flex justify-end pt-8'>
          <a href={pdfUrl} target='_blank' rel='noreferrer'>
            <Button LeftIcon='open_in_new' variant='text'>
              Abrir em nova guia
            </Button>
          </a>
        </div>
      )}
    </div>
  );
});

export default Material;
